import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import '../scss/Layout.scss';
import CallController from '../API/CallController';
import '../scss/responsive.scss';

interface Category {
  id: number;
  name: string;
  parentCategoryId: number | null;
}

interface Product {
  id: number;
  name: string;
  categoryId: number;
}

const Layout: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openMobileCategories, setOpenMobileCategories] = useState<number[]>([]);

  // Fetch categories and products on component mount
  useEffect(() => {
    fetch(CallController.GetAllCategories)
      .then(res => res.json())
      .then(data => setCategories(data))
      .catch(console.error);

    fetch(CallController.GetAllProducts)
      .then(res => res.json())
      .then(data => setProducts(data))
      .catch(console.error);
  }, []);

  // Separate main categories (no parent) and subcategories (have parent)
  const mainCategories = categories.filter(cat => cat.parentCategoryId === null);
  const subCategories = categories.filter(cat => cat.parentCategoryId !== null);

  // Update search results whenever search term, products or categories change
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      return;
    }

    const lowerTerm = searchTerm.toLowerCase();

    // Find matching products by name
    const matchedProducts = products.filter(p =>
      p.name.toLowerCase().includes(lowerTerm)
    );

    // Find matching categories by name, excluding main categories and some special ones
    const matchedCategories = categories.filter(c =>
      c.name.toLowerCase().includes(lowerTerm) &&
      c.parentCategoryId !== null && // Exclude main categories
      !['kampanj', 'om oss', 'kontakta oss'].includes(c.name.toLowerCase()) // Also exclude these names
    );

    // Combine products and categories into one search results array with type tags
    setSearchResults([
      ...matchedProducts.map(p => ({ type: 'product', data: p })),
      ...matchedCategories.map(c => ({ type: 'category', data: c }))
    ]);
  }, [searchTerm, products, categories]);

  //Subcategories for smaller screens
  const toggleMobileCategory = (id: number) => {
    if (openMobileCategories.includes(id)) {
      setOpenMobileCategories(openMobileCategories.filter(catId => catId !== id));
    } else {
      setOpenMobileCategories([...openMobileCategories, id]);
    }
  };

  return (
    <div>
      {/* different menu for phones */}
      <div className="mobile-category-menu">
        <button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Visa kategorier"
        >
          ☰
        </button>

        {isMenuOpen && (
          <div className="mobile-category-list">
            {mainCategories.map(cat => (
              <div key={cat.id} className="mobile-category-wrapper">
                <div
                  className="mobile-category-item"
                  onClick={() => toggleMobileCategory(cat.id)}
                  style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  {cat.name}
                </div>

                {openMobileCategories.includes(cat.id) && (
                  <div className="mobile-subcategory-list">
                    {subCategories
                      .filter(sub => sub.parentCategoryId === cat.id)
                      .map(sub => (
                        <Link
                          to={`/products/${sub.id}`}
                          key={sub.id}
                          className="mobile-subcategory-item"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {sub.name}
                        </Link>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>


      <div className="home">
        {/* Hide header, category bar and footer on admin pages */}
        {!isAdminPage && (
          <>
            {/* Thin top bar */}
            <div className="top-bar">
              Grossist i italienska delikatesser · För restauranger, caféer och livsmedelsbutiker
            </div>

            <header className="home-header">
              {/* Logo linking to homepage */}
              <Link to="/" className="logo-link">Accordi</Link>

              {/* Search input and results */}
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Sök produkter..."
                  className="search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {/* Show search results dropdown if there are results */}
                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map((result, index) => {
                      if (result.type === 'product') {
                        return (
                          <Link
                            key={index}
                            to={`/product/${result.data.id}`}
                            className="search-result-item"
                            onClick={() => setSearchTerm('')} // Clear search on click
                          >
                            {result.data.name}
                          </Link>
                        );
                      }
                      if (result.type === 'category') {
                        return (
                          <Link
                            key={index}
                            to={`/products/${result.data.id}`}
                            className="search-result-item"
                            onClick={() => setSearchTerm('')} // Clear search on click
                          >
                            {result.data.name}
                          </Link>
                        );
                      }
                      return null;
                    })}
                  </div>
                )}
              </div>

              {/* Navigation links that scroll to sections on the homepage */}
              <nav className="nav-links">
                <a href="#aboutUs">Om oss</a>
                <a href="#contact">Kontakta oss</a>
              </nav>
            </header>

            {/* Category bar displaying main categories with dropdown subcategories */}
            <div className="category-bar">
              {mainCategories.map(mainCat => (
                <div key={mainCat.id} className="category-item">
                  {mainCat.name}
                  {/* Show dropdown if main category has subcategories */}
                  {subCategories.some(sub => sub.parentCategoryId === mainCat.id) && (
                    <div className="subcategory-dropdown">
                      {subCategories
                        .filter(sub => sub.parentCategoryId === mainCat.id)
                        .map(sub => (
                          <div key={sub.id} className="subcategory-section">
                            {/* Link to products filtered by subcategory */}
                            <Link to={`/products/${sub.id}`} className="subcategory-item">
                              {sub.name}
                            </Link>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Outlet to render nested routes */}
        <main className="home-main">
          <Outlet />
        </main>

        {/* Footer with company info and contact, hidden on admin pages */}
        {!isAdminPage && (
          <footer className="home-footer" id="contact">
            <div className="footer-columns">
              <div className="footer-section footer-brand">
                <Link to="/" className="footer-logo">Accordi</Link>
                <p>Italienska delikatesser för restauranger, caféer och butiker.</p>
              </div>

              <div className="footer-section">
                <h3>Snabblänkar</h3>
                <Link to="/">Startsida</Link>
                <a href="/#aboutUs">Om oss</a>
              </div>

              <div className="footer-section">
                <h3>Kontakt</h3>
                <a href="mailto:info@accordi.se">info@accordi.se</a>
                <p>Industrigatan 3-5, Norrköping</p>
              </div>
            </div>

            <div className="footer-bottom">
              © {new Date().getFullYear()} Accordi AB. Alla rättigheter reserverade.
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default Layout;